import { DocumentDuplicateIcon } from '@heroicons/react/24/solid'

import { anim, pv } from '../../motion'
import { Shape } from '../../primitives'
import { Flight } from '../../v3/figures'
import { iso } from '../../v3/iso'
import { Panel } from '../../v3/panels'
import { SPOTS } from '../../v4/world'
import { STAFF_BY_ID } from '../../v5/world'
import { Caption, Tag } from '../kit'
import { cam, cameraAt } from '../timeline'

/**
 * Extra „Ein Jahr später“: Die Stelle hat sich verändert. Mara kopiert sie,
 * die Fachbereiche machen die Anforderungsanalyse neu, das Profil 2026
 * verschiebt sich gegenüber 2025, und das Team wird neu abgeglichen.
 */

const EXPERTS = ['csLead', 'csMate', 'vLead']
const head = (id) => {
  const s = iso(STAFF_BY_ID[id].x, STAFF_BY_ID[id].y)
  return { x: s.x, y: s.y - 52 }
}
const CUBE = iso(5, 4, 3.4)

const ANALYSES = EXPERTS.flatMap((id, i) =>
  [0, 1].map((k) => ({
    kind: ['tri', 'sq', 'dot'][(i + k) % 3],
    color: ['yellow', 'green', 'pink'][(i + k) % 3],
    legs: [
      {
        from: head(id),
        to: CUBE,
        at: [0.3 + i * 0.025 + k * 0.015, 0.4 + i * 0.025 + k * 0.015],
        arc: 90,
        spin: 180
      }
    ]
  }))
)

const ROWS = [
  { name: 'Emotionswahrnehmung', a: [60, 85], b: [60, 86], trend: 'same' },
  { name: 'Problemlösefähigkeit', a: [38, 60], b: [58, 84], trend: 'up' },
  { name: 'Offenheit für Neues', a: [34, 58], b: [56, 82], trend: 'up' },
  { name: 'Gewissenhaftigkeit', a: [54, 80], b: [52, 78], trend: 'same' },
  { name: 'Merkfähigkeit', a: [56, 82], b: [38, 64], trend: 'down' }
]
const TREND = { up: '↑ wichtiger', down: '↓ weniger wichtig', same: 'gleich' }

// Ideal- und Übergangsbereich wandern mit --m von 2025 nach 2026
const moving = ([a0, a1], [b0, b1], pad = 0) => ({
  left: `calc(${a0 - pad}% + ${b0 - a0}% * var(--m))`,
  width: `calc(${a1 - a0 + 2 * pad}% + ${b1 - a1 - (b0 - a0)}% * var(--m))`
})

const fade = (at, out) => ({
  ...pv('f', at),
  ...(out ? pv('g', out) : { '--g': 0 }),
  opacity: 'calc(var(--f) * (1 - var(--g)))'
})

const STAFF = [
  { at: STAFF_BY_ID.csLead, text: '88 → 86 · Passt weiter', tone: 'green' },
  {
    at: STAFF_BY_ID.csMate,
    text: '84 → 68 · Schulung anbieten',
    tone: 'yellow'
  },
  { at: STAFF_BY_ID.csNew, text: '79 → 54 · Gespräch führen', tone: 'pink' },
  { at: SPOTS.csSeat, text: '91 → 89 · Passt weiter', tone: 'green' }
]

export function WandelWorld() {
  return (
    <>
      <svg className='off-svg kx-svg' aria-hidden='true'>
        <Flight
          items={ANALYSES}
          render={(it) => (
            <g transform='translate(-9 -9)'>
              <Shape kind={it.kind} color={it.color} size={18} />
            </g>
          )}
        />
      </svg>
      <div className='off-overlay'>
        {EXPERTS.map((id, i) => (
          <Tag
            key={id}
            at3={[STAFF_BY_ID[id].x, STAFF_BY_ID[id].y, 0]}
            dy={86}
            show={[0.2 + i * 0.02, 0.26 + i * 0.02]}
            hide={[0.42, 0.46]}
            tone='green'
          >
            Anforderungsanalyse 2026
          </Tag>
        ))}
        {STAFF.map((s, i) => (
          <Tag
            key={s.text}
            at3={[s.at.x, s.at.y, 0]}
            dy={i === 3 ? 112 : 86}
            show={[0.72 + i * 0.03, 0.78 + i * 0.03]}
            hide={[0.93, 0.97]}
            tone={s.tone}
          >
            {s.text}
          </Tag>
        ))}
      </div>
    </>
  )
}

export function WandelScreen() {
  return (
    <>
      <div
        className='kx-year'
        style={anim({ at: [0.03, 0.08], out: [0.17, 0.21], s: 0.8, y: 20 })}
      >
        <small>Ein Jahr später</small>
        <div className='kx-year__num'>
          <span style={fade([-1, 0], [0.09, 0.13])}>2025</span>
          <span style={fade([0.1, 0.14])}>2026</span>
        </div>
      </div>

      <Panel at={[0.42, 0.48]} out={[0.92, 0.97]} className='off-doc kx-wandel'>
        <div className='kx-wandel__head'>
          <div>
            <div className='off-doc__eyebrow'>Anforderungsprofil</div>
            <div className='off-doc__title'>Customer Success Manager:in</div>
          </div>
          <div className='kx-years'>
            <span>
              <b>2025</b>
              <i style={fade([-1, 0], [0.5, 0.56])}>2025</i>
            </span>
            <span>
              <b>2026</b>
              <i style={fade([0.5, 0.56])}>2026</i>
            </span>
          </div>
        </div>
        <div className='kx-copy' style={fade([0.44, 0.5])}>
          <DocumentDuplicateIcon width={14} height={14} /> Stelle kopiert,
          Anforderungsanalyse 2026 abgeschlossen
        </div>
        <div style={pv('m', [0.5, 0.62])}>
          {ROWS.map((r, i) => (
            <div key={r.name} className='kx-row'>
              <i
                className='kx-row__hl'
                style={r.trend === 'same' ? { opacity: 0 } : fade([0.62, 0.68])}
              />
              <div className='kx-row__head'>
                <span>{r.name}</span>
                <em
                  className={`kx-trend kx-trend--${r.trend}`}
                  style={fade([0.62 + i * 0.012, 0.68 + i * 0.012])}
                >
                  {TREND[r.trend]}
                </em>
              </div>
              <div className='kx-beam'>
                <i className='kx-beam__trans' style={moving(r.a, r.b, 10)} />
                <i className='kx-beam__ideal' style={moving(r.a, r.b)} />
                <i
                  className='kx-beam__ghost'
                  style={{
                    left: `${r.a[0]}%`,
                    width: `${r.a[1] - r.a[0]}%`,
                    ...fade([0.5, 0.56])
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className='off-legend'>
          <span>
            <i style={{ background: 'rgb(167 247 231)' }} /> Idealbereich
          </span>
          <span>
            <i style={{ background: 'rgb(253 218 148)' }} /> Übergangsbereich
          </span>
          <span style={fade([0.5, 0.56])}>
            <i className='kx-legend-ghost' /> 2025
          </span>
        </div>
      </Panel>

      <Caption show={[0.04, 0.1]} hide={[0.18, 0.22]} label='Ein Jahr später'>
        Ein Jahr später hat sich die Rolle im Customer Success verändert. Passt
        das Anforderungsprofil noch?
      </Caption>
      <Caption
        show={[0.22, 0.27]}
        hide={[0.42, 0.46]}
        label='Ein Jahr später'
        title='Neue Analyse, gleiche Stelle.'
        short='Mara kopiert die Stelle, die Fachbereiche schätzen sie neu ein.'
      >
        Mara kopiert die Stelle und startet die Anforderungsanalyse neu. Die
        Fachbereiche schätzen die Rolle so ein, wie sie heute ist.
      </Caption>
      <Caption
        show={[0.46, 0.52]}
        hide={[0.68, 0.72]}
        label='Ein Jahr später'
        title='So stark hat sich die Stelle bewegt.'
        short='Problemlösefähigkeit und Offenheit für Neues sind wichtiger geworden, Merkfähigkeit weniger.'
      >
        Der Vergleich zeigt, wie stark sich die Stelle bewegt hat.
        Problemlösefähigkeit und Offenheit für Neues sind wichtiger geworden,
        Merkfähigkeit weniger.
      </Caption>
      <Caption
        show={[0.72, 0.76]}
        hide={[0.93, 0.97]}
        label='Ein Jahr später'
        title='Was heißt das fürs Team?'
        short='Wo Entwicklungsbedarf entsteht, helfen Schulungen oder ein ehrliches Gespräch.'
      >
        Gleiche das Team mit dem neuen Profil ab. Wo Entwicklungsbedarf
        entsteht, bietest du Schulungen an oder klärst im Gespräch, ob die Rolle
        noch passt.
      </Caption>
    </>
  )
}

export const WANDEL_EXTRA = {
  id: 'wandel',
  after: 'support',
  len: 460,
  label: 'Ein Jahr später',
  title: 'Ein Jahr später: 2025 vs. 2026',
  teaser:
    'Die Fachbereiche schätzen die Stelle neu ein. Du siehst, was sich verändert hat, und planst Schulungen oder Gespräche.',
  camera: (p, base) =>
    cameraAt(
      [
        { p: 0, cam: base },
        { p: 0.08, cam: cam([13, 11, 2], 0.88) },
        { p: 0.4, cam: cam([13, 11, 2], 0.88) },
        { p: 0.47, cam: cam([15, 11, 1.5], 1.0, -0.22) },
        { p: 0.92, cam: cam([15, 11, 1.5], 1.0, -0.22) },
        { p: 1, cam: base }
      ],
      p
    ),
  World: WandelWorld,
  Screen: WandelScreen
}
